import React, { useEffect, useMemo, useRef, useState } from "react";
import { Select, Input, message } from "antd";
import {
  BoldOutlined,
  UnderlineOutlined,
  ItalicOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  MinusOutlined,
  PlusOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import ComposeStyle from "./ComposeStyle";
import useMailbox from "./useMailbox";
import FetchData from "../../../hooks/FetchData";
import PostUpdateData from "../../../hooks/postUpdate";
import { ReactComponent as PaperclipIcon } from "../../../assets/Paperclip_Attechment_Tilt.svg";

// "04th Mar, 2024 ; 10:20 AM" - matches the original-message header shown
// above both the Reply and Forward composers in the Figma design.
const formatMessageDateTime = (value) => {
  const date = dayjs(value);
  if (!date.isValid()) return "";
  const day = date.date();
  const suffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
      ? "nd"
      : day % 10 === 3 && day !== 13
      ? "rd"
      : "th";
  return `${String(day).padStart(2, "0")}${suffix} ${date.format("MMM, YYYY ; hh:mm A")}`;
};

// Font size stepper in the editor toolbar ("-  14  +" in the design).
const DEFAULT_FONT_SIZE = 14;
const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 72;
const clampFontSize = (size) => Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, size));

// execCommand("fontSize") only knows the legacy 1-7 scale, so the editor
// applies size 7 as a marker and immediately swaps every <font size="7">
// for a <span> with the real pixel size.
const FONT_SIZE_MARKER = "7";

const FORMAT_BUTTONS = [
  { command: "bold", title: "Bold", icon: <BoldOutlined /> },
  { command: "underline", title: "Underline", icon: <UnderlineOutlined /> },
  { command: "italic", title: "Italic", icon: <ItalicOutlined /> },
  { command: "insertOrderedList", title: "Numbered list", icon: <OrderedListOutlined /> },
  { command: "insertUnorderedList", title: "Bullet list", icon: <UnorderedListOutlined /> },
];

// Size of the text the selection actually covers. The anchor node alone
// isn't enough: Ctrl+A (or selecting from the very start) anchors on the
// editor box itself, whose size is always the 14px default, so "+" would
// count up from 14 instead of the selected text's real size.
const getSelectionFontSize = (range) => {
  let element = range.startContainer;
  if (!range.collapsed) {
    const walker = document.createTreeWalker(range.commonAncestorContainer, NodeFilter.SHOW_TEXT);
    let node = walker.currentNode.nodeType === Node.TEXT_NODE ? walker.currentNode : walker.nextNode();
    while (node) {
      if (node.textContent.trim() && range.intersectsNode(node)) {
        element = node;
        break;
      }
      node = walker.nextNode();
    }
  }
  if (element.nodeType === Node.TEXT_NODE) element = element.parentElement;
  return Math.round(parseFloat(window.getComputedStyle(element).fontSize));
};

const Compose = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { messageId } = useParams();
  const clientId = useSelector((state) => state.authTracking.userInfo?.clientId);
  const role = useSelector((state) => state.systemTracking.role);
  const viewingAsPeopleId = useSelector((state) => state.systemTracking.viewingAsPeopleId);
  // Replies/forwards go out from whichever mailbox is being viewed.
  const { query: mailboxQuery } = useMailbox();

  // One component drives all three screens from the Figma design
  // (Sending new message / Replying to message / Forwarding message) -
  // they only differ in which fields show and what gets submitted where.
  const mode = location.pathname.includes("/reply/")
    ? "reply"
    : location.pathname.includes("/forward/")
    ? "forward"
    : "compose";

  const [people, setPeople] = useState([]);
  const [originalMessage, setOriginalMessage] = useState(null);
  const [recipientIds, setRecipientIds] = useState([]);
  const [subject, setSubject] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!clientId) return;
    FetchData({ endPoint: `/people/getuser/${clientId}` })
      .then((res) => setPeople(res?.response || []))
      .catch(() => {});
  }, [clientId]);

  useEffect(() => {
    if (!clientId || mode === "compose" || !messageId) return;
    FetchData({ endPoint: `/message/getmessage/${clientId}/${messageId}${mailboxQuery}` })
      .then((res) => {
        const original = res?.response;
        setOriginalMessage(original);
        if (mode === "forward" && original?.subject) {
          setSubject(
            original.subject.startsWith("Forward:") ? original.subject : `Forward: ${original.subject}`
          );
        }
      })
      .catch(() => message.error("Failed to load the original message"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, mode, messageId, mailboxQuery]);

  const peopleOptions = useMemo(
    () =>
      people.map((person) => ({
        value: person.people_id,
        label: `${person.first_name || ""} ${person.last_name || ""}`.trim() || person.email,
      })),
    [people]
  );

  // Header's "View as" picker (role switcher) is the only real person
  // identity this app ever has - People have no login of their own to
  // call this API directly. Composing while viewing as someone sends the
  // message as that person, straight to the Admin's own Inbox, instead
  // of the normal Admin-to-people compose flow.
  const isSendingAsPerson = mode === "compose" && role === "Learner" && !!viewingAsPeopleId;
  const viewingAsPerson = people.find((person) => person.people_id === viewingAsPeopleId);
  const viewingAsPersonName = viewingAsPerson
    ? `${viewingAsPerson.first_name || ""} ${viewingAsPerson.last_name || ""}`.trim()
    : "";

  const [fontSize, setFontSize] = useState(DEFAULT_FONT_SIZE);
  const [fontSizeInput, setFontSizeInput] = useState(String(DEFAULT_FONT_SIZE));
  const [activeFormats, setActiveFormats] = useState({});
  // Last caret/selection inside the editor - typing in the font size box
  // moves focus out of the editor, so it's restored before applying.
  const savedRangeRef = useRef(null);
  // Size the next marker should become. A ref, not state: the input handler
  // must see the value from the latest click immediately, not the one
  // captured in an older render.
  const pendingFontSizeRef = useRef(DEFAULT_FONT_SIZE);

  const isInsideEditor = (node) => !!node && !!editorRef.current?.contains(node);

  // Keeps the toolbar in sync with wherever the caret is: B/U/I/list
  // highlight when active, and the size box shows the text's actual size.
  useEffect(() => {
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || !isInsideEditor(selection.anchorNode)) return;
      // Never read a size off a not-yet-converted marker (it's 48px).
      convertFontSizeMarkers();
      savedRangeRef.current = selection.getRangeAt(0).cloneRange();

      const nextActive = {};
      FORMAT_BUTTONS.forEach(({ command }) => {
        nextActive[command] = document.queryCommandState(command);
      });
      setActiveFormats(nextActive);

      const size = getSelectionFontSize(selection.getRangeAt(0));
      if (size) {
        setFontSize(size);
        setFontSizeInput(String(size));
      }
    };
    document.addEventListener("selectionchange", handleSelectionChange);
    return () => document.removeEventListener("selectionchange", handleSelectionChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only re-applies the saved range when focus actually left the editor
  // (e.g. the font size box) - re-adding a range the editor still has
  // would wipe pending caret styles like "next text is 16px".
  const restoreEditorSelection = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const current = window.getSelection();
    const selectionStillInEditor =
      document.activeElement === editor && current?.rangeCount > 0 && isInsideEditor(current.anchorNode);
    if (selectionStillInEditor) return;
    editor.focus();
    if (savedRangeRef.current && isInsideEditor(savedRangeRef.current.startContainer)) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedRangeRef.current);
    }
  };

  // Converted in place (swap the attribute for an inline style) rather than
  // moving the text into a new element - moving nodes resets the caret to
  // the start of the line and scrambles whatever is typed next. Any older
  // size set inside the marker is cleared, or it would override the new one
  // and "+" would appear to do nothing on already-resized text.
  const convertFontSizeMarkers = () => {
    editorRef.current?.querySelectorAll(`font[size="${FONT_SIZE_MARKER}"]`).forEach((fontEl) => {
      fontEl.removeAttribute("size");
      fontEl.style.fontSize = `${pendingFontSizeRef.current}px`;
      fontEl.querySelectorAll("[style]").forEach((inner) => {
        inner.style.fontSize = "";
        if (!inner.getAttribute("style")) inner.removeAttribute("style");
      });
    });
  };

  const applyFormat = (command) => {
    restoreEditorSelection();
    document.execCommand(command);
    setActiveFormats((prev) => ({ ...prev, [command]: document.queryCommandState(command) }));
  };

  const applyFontSize = (size) => {
    const nextSize = clampFontSize(size);
    pendingFontSizeRef.current = nextSize;
    setFontSize(nextSize);
    setFontSizeInput(String(nextSize));
    restoreEditorSelection();
    document.execCommand("styleWithCSS", false, false);
    document.execCommand("fontSize", false, FONT_SIZE_MARKER);
    convertFontSizeMarkers();
  };

  // Blur fires whenever focus leaves the size box (e.g. clicking back into
  // the editor) - only apply if the number was actually changed.
  const commitFontSizeInput = () => {
    const typed = parseInt(fontSizeInput, 10);
    if (Number.isNaN(typed) || typed === fontSize) {
      setFontSizeInput(String(fontSize));
      return;
    }
    applyFontSize(typed);
  };

  // With a collapsed caret the browser only creates the <font size="7">
  // once the next character is typed, so markers are also swapped on input.
  const handleEditorInput = () => convertFontSizeMarkers();

  // Toolbar buttons must not steal focus from the editor, or the
  // selection they're meant to format is lost before the click lands.
  const keepEditorFocus = (event) => event.preventDefault();

  const handleAttachClick = () => fileInputRef.current?.click();

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !clientId) return;

    setUploading(true);
    try {
      const urlResponse = await PostUpdateData({
        endPoint: `/message/getuploadurl/${clientId}`,
        method: "POST",
        obj: { file_name: file.name, content_type: file.type },
      });
      const { uploadUrl, fileUrl } = urlResponse?.response || {};
      if (!uploadUrl || !fileUrl) {
        throw new Error("No upload URL returned");
      }

      // Straight to S3 with the presigned URL - no auth header, same as
      // CreateCourse.js's unit media upload.
      const uploadResult = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!uploadResult.ok) {
        throw new Error(`S3 upload failed with status ${uploadResult.status}`);
      }

      setAttachments((prev) => [
        ...prev,
        { file_name: file.name, file_url: fileUrl, file_size: file.size, file_type: file.type },
      ]);
    } catch (error) {
      console.error("Error uploading attachment:", error);
      message.error("Failed to upload attachment");
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (fileUrl) => {
    setAttachments((prev) => prev.filter((file) => file.file_url !== fileUrl));
  };

  const goBackToInbox = () => navigate("/messages");

  const handleSend = async () => {
    const bodyHtml = editorRef.current?.innerHTML?.trim();
    if (!bodyHtml) {
      message.error("Message can't be empty");
      return;
    }
    if (mode !== "reply" && !isSendingAsPerson && recipientIds.length === 0) {
      message.error("Pick at least one recipient");
      return;
    }
    if (mode === "compose" && !subject.trim()) {
      message.error("Subject is required");
      return;
    }

    setSending(true);
    try {
      if (isSendingAsPerson) {
        await PostUpdateData({
          endPoint: `/message/sendasperson/${clientId}`,
          method: "POST",
          obj: {
            people_id: viewingAsPeopleId,
            subject: subject.trim(),
            message_body: bodyHtml,
            attachments,
          },
        });
        message.success("Message sent successfully");
      } else if (mode === "compose") {
        await PostUpdateData({
          endPoint: `/message/sendmessage/${clientId}`,
          method: "POST",
          obj: {
            recipient_people_ids: recipientIds,
            subject: subject.trim(),
            message_body: bodyHtml,
            attachments,
          },
        });
        message.success("Message sent successfully");
      } else if (mode === "reply") {
        await PostUpdateData({
          endPoint: `/message/replymessage/${clientId}/${messageId}${mailboxQuery}`,
          method: "POST",
          obj: { message_body: bodyHtml, attachments },
        });
        message.success("Reply sent successfully");
      } else {
        await PostUpdateData({
          endPoint: `/message/forwardmessage/${clientId}/${messageId}${mailboxQuery}`,
          method: "POST",
          obj: {
            recipient_people_ids: recipientIds,
            subject: subject.trim() || undefined,
            message_body: bodyHtml,
            attachments,
          },
        });
        message.success("Message forwarded successfully");
      }
      // Land where the message actually shows up: the sender's own Sent tab
      // (Admin's, or the View As person's) - it's in the recipient's Inbox,
      // never the sender's.
      navigate("/messages?tab=sent");
    } catch (error) {
      console.error("Error sending message:", error);
      message.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const screenTitle =
    mode === "reply" ? "Replying to message" : mode === "forward" ? "Forwarding message" : "Sending new message";

  return (
    <ComposeStyle>
      <p className="compose-breadcrumb">
        <span className="crumb-link" onClick={goBackToInbox}>
          {mode === "compose" ? "Message" : "Inbox"}
        </span>
        {" > "}
        <span className="crumb-current">{screenTitle}</span>
      </p>

      {(mode === "reply" || mode === "forward") && originalMessage && (
        <div className="original-message-block">
          <div className="original-message-header">
            <div className="original-message-avatar">
              {(originalMessage.sender_name || "?").charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="original-message-from">From: {originalMessage.sender_name}</p>
              <p className="original-message-date">{formatMessageDateTime(originalMessage.created_at)}</p>
            </div>
          </div>
          <p className="original-message-subject">{originalMessage.subject}</p>
          <div
            className="original-message-body"
            dangerouslySetInnerHTML={{ __html: originalMessage.body }}
          />
        </div>
      )}

      {mode !== "reply" && isSendingAsPerson && (
        <div className="compose-field">
          <label className="compose-field-label">Sending as</label>
          <Input className="compose-subject-input" value={viewingAsPersonName} disabled />
        </div>
      )}

      {mode !== "reply" && !isSendingAsPerson && (
        <div className="compose-field">
          <label className="compose-field-label">{mode === "forward" ? "Forward to" : "Send to"}</label>
          <Select
            className="compose-recipients"
            mode="multiple"
            style={{ width: "100%" }}
            placeholder="Select people"
            value={recipientIds}
            onChange={setRecipientIds}
            options={peopleOptions}
            optionFilterProp="label"
          />
        </div>
      )}

      {mode !== "reply" && (
        <div className="compose-field">
          <label className="compose-field-label">Subject</label>
          <Input
            className="compose-subject-input"
            placeholder="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>
      )}

      <div className="compose-field">
        <label className="compose-field-label">
          {mode === "reply" && originalMessage ? `Replying to ${originalMessage.sender_name}` : "Message"}
        </label>
        <div className="compose-editor-wrapper">
          <div className="compose-editor-toolbar">
            <button
              type="button"
              className="compose-editor-toolbar-btn"
              title="Decrease font size"
              onMouseDown={keepEditorFocus}
              onClick={() => applyFontSize(fontSize - 1)}
              disabled={fontSize <= MIN_FONT_SIZE}
            >
              <MinusOutlined />
            </button>
            <input
              className="compose-font-size-input"
              aria-label="Font size"
              value={fontSizeInput}
              onChange={(e) => setFontSizeInput(e.target.value.replace(/[^0-9]/g, ""))}
              onBlur={commitFontSizeInput}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitFontSizeInput();
                }
              }}
            />
            <button
              type="button"
              className="compose-editor-toolbar-btn"
              title="Increase font size"
              onMouseDown={keepEditorFocus}
              onClick={() => applyFontSize(fontSize + 1)}
              disabled={fontSize >= MAX_FONT_SIZE}
            >
              <PlusOutlined />
            </button>
            {FORMAT_BUTTONS.map(({ command, title, icon }) => (
              <button
                key={command}
                type="button"
                title={title}
                className={`compose-editor-toolbar-btn${activeFormats[command] ? " active" : ""}`}
                onMouseDown={keepEditorFocus}
                onClick={() => applyFormat(command)}
              >
                {icon}
              </button>
            ))}
            <button
              type="button"
              className="compose-attach-btn"
              onMouseDown={keepEditorFocus}
              onClick={handleAttachClick}
              disabled={uploading}
            >
              <PaperclipIcon className="compose-attach-icon" width={24} height={24} />
              {uploading ? "Uploading..." : "Attach File"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: "none" }}
              onChange={handleFileSelected}
            />
          </div>
          <div
            ref={editorRef}
            className="compose-editor-body"
            contentEditable
            onInput={handleEditorInput}
            data-placeholder="Write your message..."
            suppressContentEditableWarning
          />
          {attachments.length > 0 && (
            <div className="compose-attachments">
              {attachments.map((file) => (
                <div className="compose-attachment-chip" key={file.file_url}>
                  <span className="compose-attachment-name">{file.file_name}</span>
                  <CloseOutlined
                    className="compose-attachment-remove"
                    onClick={() => removeAttachment(file.file_url)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="compose-footer">
        <button type="button" className="compose-cancel" onClick={goBackToInbox}>
          Cancel
        </button>
        <button type="button" className="compose-send" onClick={handleSend} disabled={sending}>
          Send
        </button>
      </div>
    </ComposeStyle>
  );
};

export default Compose;
